import { CareerService, RoleLibraryService } from '@careerhub/api';
import { all, call, fork, getContext, put, select, takeLeading } from 'redux-saga/effects';
import {
  CareerActionType,
  addBookmarkFailed,
  addBookmarkSuccess,
  loadBookmarksFailed,
  loadBookmarksSuccess,
  removeBookmarkFailed,
  removeBookmarkSuccess,
  loadTaxonomyTypeSuccess,
  loadTaxonomyTypeFailed,
} from './actions';
import { IActionWithParam, LoadingStates } from '../common.types';
import { Awaited } from '../../types';
import { careerStateSelector } from './reducers';
import { genericErrorMessage } from '../../Constants';

function* handleFetchBookmarks() {
  // If we've already tried to fetch bookmarks, don't fetch again.
  // User can refresh the page to get updated bookmarks from service.
  const careerState = (yield select(careerStateSelector)) as ReturnType<typeof careerStateSelector>;
  if (careerState.fetchBookmarksStatus !== LoadingStates.STARTED) {
    return;
  }

  // Fetch bookmarks from service
  const careerService: CareerService = yield getContext('careerService');
  try {
    const getBookmarks = async () => {
      return await careerService.getBookmarks();
    };
    const bookmarks: Awaited<ReturnType<typeof getBookmarks>> = yield call(getBookmarks);
    yield put(loadBookmarksSuccess(bookmarks));
  } catch (ex) {
    yield put(loadBookmarksFailed(ex?.data?.errorMessage ?? 'Something went wrong. Please try again later.'));
  }
}

function* handleAddBookmark(action: IActionWithParam<string>) {
  const careerService: CareerService = yield getContext('careerService');
  try {
    const addBookmark = async () => {
      return await careerService.addBookmark(action.param);
    };
    const id: Awaited<ReturnType<typeof addBookmark>> = yield call(addBookmark);
    yield put(addBookmarkSuccess(id));
  } catch (ex) {
    yield put(addBookmarkFailed(ex?.data?.errorMessage ?? genericErrorMessage));
  }
}
function* handleFetchTaxonomyType() {
  const roleLibraryService: RoleLibraryService = yield getContext('roleLibraryService');
  try {
    const getTaxonomyTypes = async () => {
      return await roleLibraryService.getTaxonomyTypes();
    };
    const taxonomyTypes: Awaited<ReturnType<typeof getTaxonomyTypes>> = yield call(getTaxonomyTypes);
    yield put(loadTaxonomyTypeSuccess(taxonomyTypes));
  } catch (ex) {
    yield put(loadTaxonomyTypeFailed(ex?.data?.errorMessage ?? genericErrorMessage));
  }
}

function* handleRemoveBookmark(action: IActionWithParam<string>) {
  const careerService: CareerService = yield getContext('careerService');
  try {
    const removeBookmark = async () => {
      return await careerService.removeBookmark(action.param);
    };
    const id: Awaited<ReturnType<typeof removeBookmark>> = yield call(removeBookmark);
    yield put(removeBookmarkSuccess(id));
  } catch (ex) {
    yield put(removeBookmarkFailed(ex?.data?.errorMessage ?? genericErrorMessage));
  }
}

export function* watchFetchBookmarks(): IterableIterator<{}> {
  yield takeLeading(CareerActionType.LOAD_BOOKMARKS, handleFetchBookmarks);
}

export function* watchAddBookmark(): IterableIterator<{}> {
  yield takeLeading(CareerActionType.ADD_BOOKMARK, handleAddBookmark);
}

export function* watchAddTaxonomyType(): IterableIterator<{}> {
  yield takeLeading(CareerActionType.LOAD_TAXONOMYTYPE, handleFetchTaxonomyType);
}

export function* watchRemoveBookmark(): IterableIterator<{}> {
  yield takeLeading(CareerActionType.REMOVE_BOOKMARK, handleRemoveBookmark);
}

export function* careerSagas() {
  yield all([fork(watchFetchBookmarks)]);
  yield all([fork(watchAddBookmark)]);
  yield all([fork(watchAddTaxonomyType)]);
  yield all([fork(watchRemoveBookmark)]);
}
